import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AlwaysError } from '../../../core/services/always-error.service';

export interface MultiSelectOption {
  id: string;
  label: string;
}

@Component({
  selector: 'osn-cxb-api-select-tag-control',
  templateUrl: './api-select-tag-control.component.html',
  styleUrls: ['./api-select-tag-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ApiSelectTagControlComponent),
      multi: true,
    },
  ],
})
export class ApiSelectTagControlComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() public placeholder: string;
  @Input() public filterNoEntriesFoundLabel = 'No entries found';
  @Input() public filterPlaceholderLabel = 'Filter entries...';
  @Input() public isInitialLoading$: Observable<boolean>;
  @Input() public isFilterLoading$: Observable<boolean>;
  @Input() public isLoading$: Observable<boolean>;
  @Input() public hintLabel: string;
  @Input() public errorLabel: string;
  @Input() public label: string;
  @Input() public availableOptions: MultiSelectOption[];
  @Input() public selectedOptions: MultiSelectOption[];
  public formControl: FormControl<string[]>;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('data-testid') public testId = 'api-select';
  @Output() public searchValueChanged: EventEmitter<string> = new EventEmitter();

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};
  private readonly destroyed$ = new Subject<void>();

  public filterControl: FormControl<string> = new FormControl('');

  private readonly DEBOUNCE_TIME = 500;
  private readonly CHARACTER_LIMIT = 3;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef, public readonly alwaysError: AlwaysError) {}

  public ngOnInit() {
    this.filterControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.DEBOUNCE_TIME),
        filter(({ length }) => length >= this.CHARACTER_LIMIT)
      )
      .subscribe((searchValue) => {
        this.searchValueChanged.emit(searchValue);
      });
  }

  public writeValue(value: string[]) {
    this.formControl = new FormControl<string[]>(value || []);
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => this.changeDetectorRef.detectChanges())
      )
      .subscribe((actorIds) => this.propagateChange(actorIds));
  }

  public addOption(optionToAdd: MultiSelectOption): void {
    const updatedValue = [...this.formControl.value, optionToAdd.id];
    this.formControl.setValue(updatedValue);

    this.selectedOptions = [...this.selectedOptions, optionToAdd];
    this.availableOptions = [];
    this.filterControl.setValue('');
  }

  public removeOption(idToRemove: string) {
    const updatedValue = this.formControl.value.filter((selectedOption) => selectedOption !== idToRemove);
    this.formControl.setValue(updatedValue);
    this.selectedOptions = this.selectedOptions.filter(({ id }) => id !== idToRemove);
    this.changeDetectorRef.detectChanges();
  }

  public registerOnChange(onChange: Function): void {
    this.propagateChange = onChange;
  }

  public registerOnTouched(onTouched: Function): void {
    this.propagateTouch = onTouched;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
